import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';

const initialFormState = {
  fullName: '',
  email: '',
  accountId: '',
  topic: 'technical',
  urgency: 'normal',
  summary: '',
  details: '',
  reproduction: ''
};

const SupportPage = () => {
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.fullName.trim()) {nextErrors.fullName = 'Name is required.';}
    if (!formData.email.trim()) {
      nextErrors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      nextErrors.email = 'Enter a valid email address.';
    }
    if (!formData.summary.trim()) {nextErrors.summary = 'Add a brief subject for your request.';}
    if (!formData.details.trim()) {nextErrors.details = 'Please describe the issue or request.';}
    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Please complete the required fields.');
      return;
    }

    const topicLabels = {
      technical: 'Technical Issue',
      billing: 'Billing & Account',
      onboarding: 'Onboarding / Setup',
      feedback: 'Product Feedback',
      other: 'Other'
    };

    const urgencyLabels = {
      urgent: 'Urgent',
      high: 'High',
      normal: 'Normal',
      low: 'Low'
    };

    const subject = `Support Request – ${topicLabels[formData.topic]} (${urgencyLabels[formData.urgency]})`;
    const bodyLines = [
      `Name: ${formData.fullName}`,
      `Email: ${formData.email}`,
      `Account ID / Company: ${formData.accountId || 'N/A'}`,
      `Topic: ${topicLabels[formData.topic]}`,
      `Urgency: ${urgencyLabels[formData.urgency]}`,
      `Summary: ${formData.summary}`,
      '',
      'Details:',
      formData.details,
      '',
      'Steps to Reproduce / Additional Context:',
      formData.reproduction || 'None provided'
    ];

    const mailtoLink = `mailto:hello@relaywork.co?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines.join('\n'))}`;

    toast.success('Launching your email client to send the support request...');
    window.location.href = mailtoLink;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10">
          <div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-3">Alfred Voice Support</h1>
            <p className="text-gray-600 max-w-2xl">
              Submit a request to the Alfred Voice support team. Complete the form below and we will follow up within 24 hours on business days.
            </p>
          </div>
          <Link
            to="/affiliates/apply"
            className="inline-flex items-center text-sm font-semibold text-indigo-600 hover:text-indigo-500"
          >
            Affiliate application →
          </Link>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white rounded-3xl shadow-xl border border-gray-100 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-semibold text-gray-700 mb-2">
                    Full name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="Alex Morgan"
                    className={`w-full rounded-xl border px-4 py-3 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 ${errors.fullName ? 'border-rose-400 focus:ring-rose-400' : 'border-gray-200'}`}
                  />
                  {errors.fullName && <p className="mt-1 text-sm text-rose-500">{errors.fullName}</p>}
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Work email <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@company.com"
                    className={`w-full rounded-xl border px-4 py-3 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 ${errors.email ? 'border-rose-400 focus:ring-rose-400' : 'border-gray-200'}`}
                  />
                  {errors.email && <p className="mt-1 text-sm text-rose-500">{errors.email}</p>}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="accountId" className="block text-sm font-semibold text-gray-700 mb-2">
                    Account ID or company (optional)
                  </label>
                  <input
                    id="accountId"
                    name="accountId"
                    value={formData.accountId}
                    onChange={handleChange}
                    placeholder="AV-001234"
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="topic" className="block text-sm font-semibold text-gray-700 mb-2">
                      Topic
                    </label>
                    <select
                      id="topic"
                      name="topic"
                      value={formData.topic}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                    >
                      <option value="technical">Technical issue</option>
                      <option value="billing">Billing &amp; account</option>
                      <option value="onboarding">Onboarding / setup question</option>
                      <option value="feedback">Product feedback</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="urgency" className="block text-sm font-semibold text-gray-700 mb-2">
                      Urgency
                    </label>
                    <select
                      id="urgency"
                      name="urgency"
                      value={formData.urgency}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                    >
                      <option value="urgent">Urgent (system down)</option>
                      <option value="high">High</option>
                      <option value="normal">Normal</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="summary" className="block text-sm font-semibold text-gray-700 mb-2">
                  Request summary <span className="text-rose-500">*</span>
                </label>
                <input
                  id="summary"
                  name="summary"
                  value={formData.summary}
                  onChange={handleChange}
                  placeholder="Short description of your issue or question"
                  className={`w-full rounded-xl border px-4 py-3 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 ${errors.summary ? 'border-rose-400 focus:ring-rose-400' : 'border-gray-200'}`}
                />
                {errors.summary && <p className="mt-1 text-sm text-rose-500">{errors.summary}</p>}
              </div>

              <div>
                <label htmlFor="details" className="block text-sm font-semibold text-gray-700 mb-2">
                  Describe the issue or request <span className="text-rose-500">*</span>
                </label>
                <textarea
                  id="details"
                  name="details"
                  rows={6}
                  value={formData.details}
                  onChange={handleChange}
                  placeholder="Include as much detail as possible so our team can assist quickly."
                  className={`w-full rounded-xl border px-4 py-3 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 ${errors.details ? 'border-rose-400 focus:ring-rose-400' : 'border-gray-200'}`}
                />
                {errors.details && <p className="mt-1 text-sm text-rose-500">{errors.details}</p>}
              </div>

              <div>
                <label htmlFor="reproduction" className="block text-sm font-semibold text-gray-700 mb-2">
                  Steps to reproduce / additional context (optional)
                </label>
                <textarea
                  id="reproduction"
                  name="reproduction"
                  rows={4}
                  value={formData.reproduction}
                  onChange={handleChange}
                  placeholder="Add relevant details such as call IDs, timestamps, or steps we can follow to trace the issue."
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>

              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <p className="text-sm text-gray-500">
                  We reply from <span className="font-semibold text-gray-700">hello@relaywork.co</span>. Please attach logs or screenshots when replying to the confirmation email.
                </p>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center bg-gray-900 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:bg-gray-800 hover:-translate-y-0.5 transition-all"
                >
                  Submit support request
                </button>
              </div>
            </form>
          </div>

          <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-6 space-y-6">
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-2">Response times</h2>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><span className="font-semibold text-gray-800">Urgent:</span> &lt; 2 business hours</li>
                <li><span className="font-semibold text-gray-800">High:</span> Same business day</li>
                <li><span className="font-semibold text-gray-800">Normal:</span> Within 24 hours</li>
                <li><span className="font-semibold text-gray-800">Low:</span> Within 2 business days</li>
              </ul>
            </div>

            <div className="border-t border-gray-100 pt-4">
              <h2 className="text-lg font-bold text-gray-900 mb-2">Useful resources</h2>
              <ul className="space-y-2 text-sm text-indigo-600">
                <li>
                  <a href="https://relaywork.co/faq.html" className="hover:underline" target="_blank" rel="noreferrer">
                    Relay Work FAQ
                  </a>
                </li>
                <li>
                  <a href="https://relaywork.co/privacy.html" className="hover:underline" target="_blank" rel="noreferrer">
                    Privacy policy
                  </a>
                </li>
                <li>
                  <a href="https://relaywork.co/terms.html" className="hover:underline" target="_blank" rel="noreferrer">
                    Terms of service
                  </a>
                </li>
              </ul>
            </div>

            <div className="border-t border-gray-100 pt-4 text-sm text-gray-500">
              <p>
                For phone escalations, reply to the confirmation email with &quot;URGENT&quot; in the subject line and our on-call specialist will call the number on your account.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
